// NotificationsStep Component
// Step 4: Notification settings - user can toggle categories

import React from 'react';
import { 
  FaCheck,
  FaInfoCircle,
  FaSpinner
} from 'react-icons/fa';
import styles from './NotificationsStep.module.css';

const NotificationsStep = ({ formData, updateFormData, onSubmit, onBack, isSubmitting, submitButtonText }) => {
  const notificationCategories = [
    { id: 'program', name: 'Program Registration', icon: '📋', description: 'Applications, approvals, and enrollment' },
    { id: 'event', name: 'Events', icon: '📅', description: 'Schedules, reminders, and cancellations' },
    { id: 'rsvp', name: 'RSVP Management', icon: '✋', description: 'Attendance confirmations and changes' },
    { id: 'equipment', name: 'Equipment', icon: '🛠️', description: 'Inventory and assignment updates' },
    { id: 'team', name: 'Team', icon: '👥', description: 'Roster changes and team updates' },
    { id: 'assignment', name: 'Assignments', icon: '📝', description: 'Tasks and deadlines' }
  ];

  // Get notification settings from formData or use defaults (all enabled)
  const notificationSettings = formData.notifications || {
    categories: notificationCategories.reduce((acc, cat) => ({ ...acc, [cat.id]: true }), {})
  };

  const toggleCategory = (categoryId) => {
    updateFormData({
      notifications: {
        ...notificationSettings,
        categories: {
          ...notificationSettings.categories,
          [categoryId]: !notificationSettings.categories[categoryId]
        }
      }
    });
  };

  const enabledCount = Object.values(notificationSettings.categories || {}).filter(Boolean).length;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Notification Categories</h2>
        <p className={styles.subtitle}>Choose which notification categories to enable for your club</p>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Category Settings */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Categories</h3>
            <span className={styles.enabledCount}>{enabledCount} of {notificationCategories.length} enabled</span>
          </div>
          <div className={styles.categoriesList}>
            {notificationCategories.map(category => (
              <label key={category.id} className={styles.categoryItem}>
                <span className={styles.categoryIcon}>{category.icon}</span>
                <div className={styles.categoryInfo}>
                  <span className={styles.categoryName}>{category.name}</span>
                  <span className={styles.categoryDesc}>{category.description}</span>
                </div>
                <div className={styles.toggleWrapper}>
                  <input
                    type="checkbox"
                    className={styles.toggleInput}
                    checked={notificationSettings.categories[category.id] !== false}
                    onChange={() => toggleCategory(category.id)}
                  />
                  <div className={`${styles.toggle} ${notificationSettings.categories[category.id] !== false ? styles.toggleOn : ''}`}>
                    <div className={styles.toggleKnob} />
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className={styles.tipBanner}>
          <FaInfoCircle className={styles.tipIcon} />
          <p>
            <strong>Note:</strong> Enable or disable entire notification categories here. For granular control over individual notifications and delivery channels (In-App/Email), visit the <strong>Notification Settings</strong> page in your club's management area.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button 
          className={styles.backButton} 
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </button>
        <button
          className={styles.createButton}
          onClick={onSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <FaSpinner className={styles.spinner} />
              {submitButtonText === 'Save Changes' ? 'Saving...' : 'Creating Club...'}
            </>
          ) : (
            <>
              <FaCheck />
              {submitButtonText || 'Create Club'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default NotificationsStep;
